import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ChevronLeft, ChevronRight, RefreshCw, Sun, Moon, Calendar as CalendarIcon, Users, Clock, Target
} from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarPage = () => {
  const { user } = useAuth();
  const isManager = (user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r));
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [salesPeople, setSalesPeople] = useState([]);
  const [teamMembersList, setTeamMembersList] = useState([]);
  const [salesFilter, setSalesFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');

  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();

  useEffect(() => { fetchEvents(); }, [month, year, salesFilter, teamFilter]);

  useEffect(() => {
    if (isManager) {
      api.get('/calendar/sales-people').then(r => setSalesPeople(r.data || [])).catch(() => {});
      api.get('/calendar/team-members').then(r => setTeamMembersList(r.data || [])).catch(() => {});
    }
  }, [isManager]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      let url = `/calendar?month=${month}&year=${year}`;
      if (isManager && salesFilter !== 'all') url += `&sales_person=${salesFilter}`;
      if (isManager && teamFilter !== 'all') url += `&team_member=${teamFilter}`;
      const res = await api.get(url);
      setEvents(res.data);
    } catch (err) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1));
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month, 1));
    setSelectedDate(null);
  };

  const goToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  // Calendar grid
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date().toISOString().split('T')[0];

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(ev => {
      const evDate = ev.date?.split('T')[0];
      return evDate === dateStr;
    });
  };

  const selectedEvents = selectedDate ? events.filter(ev => ev.date?.split('T')[0] === selectedDate) : [];

  const getEventColor = (type) => {
    if (type === 'shoot') return 'bg-[#F5A623]';
    if (type === 'deadline') return 'bg-rose-500';
    if (type === 'followup') return 'bg-blue-500';
    return 'bg-zinc-500';
  };

  const getBrandColor = (brand) => brand === 'triobliss' ? 'text-pink-400' : 'text-[#F5A623]';

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Calendar</h1>
          <p className="text-zinc-500 text-sm mt-1">Events, shoots & deadlines</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isManager && salesPeople.length > 0 && (
            <select
              value={salesFilter}
              onChange={(e) => setSalesFilter(e.target.value)}
              className="bg-[#121212] border border-white/10 text-white text-sm rounded px-3 py-1.5 h-8 focus:outline-none focus:border-[#F5A623]/50"
              data-testid="calendar-sales-filter"
            >
              <option value="all">All Sales People</option>
              {salesPeople.map(sp => (
                <option key={sp.id} value={sp.id}>{sp.name}</option>
              ))}
            </select>
          )}
          {isManager && teamMembersList.length > 0 && (
            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="bg-[#121212] border border-white/10 text-white text-sm rounded px-3 py-1.5 h-8 focus:outline-none focus:border-[#F5A623]/50"
              data-testid="calendar-team-filter"
            >
              <option value="all">All Team</option>
              {teamMembersList.map(tm => (
                <option key={tm.id} value={tm.id}>{tm.name}</option>
              ))}
            </select>
          )}
          <Button variant="outline" size="sm" onClick={goToday} className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={fetchEvents} className="border-white/10 text-zinc-400 hover:bg-white/5" data-testid="refresh-calendar">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Manager Sales Summary — per sales person follow-up counts for current view */}
      {isManager && salesFilter === 'all' && (() => {
        const followups = events.filter(ev => ev.type === 'followup');
        if (followups.length === 0) return null;
        const byPerson = {};
        followups.forEach(ev => {
          const key = ev.assigned_to_name || 'Unassigned';
          if (!byPerson[key]) byPerson[key] = { count: 0, id: ev.assigned_to };
          byPerson[key].count += 1;
        });
        const entries = Object.entries(byPerson).sort((a, b) => b[1].count - a[1].count);
        return (
          <div className="bg-[#121212] border border-white/10 rounded-lg p-3" data-testid="sales-followup-summary">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Follow-ups this month by sales person</p>
            <div className="flex flex-wrap gap-2">
              {entries.map(([name, info]) => (
                <button
                  key={name}
                  onClick={() => info.id && setSalesFilter(info.id)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded border border-white/10 bg-[#0A0A0A] hover:border-[#F5A623]/40 transition-colors"
                  data-testid={`sales-summary-${name}`}
                >
                  <span className="text-sm text-white">{name}</span>
                  <Badge variant="outline" className="bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20 text-xs">{info.count}</Badge>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-2">
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
            {/* Month Nav */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <button onClick={prevMonth} className="p-1 text-zinc-400 hover:text-white rounded hover:bg-white/10 transition-colors" data-testid="prev-month">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <h2 className="text-white font-semibold">{MONTHS[month - 1]} {year}</h2>
              <button onClick={nextMonth} className="p-1 text-zinc-400 hover:text-white rounded hover:bg-white/10 transition-colors" data-testid="next-month">
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {/* Days Header */}
            <div className="grid grid-cols-7 border-b border-white/10">
              {DAYS.map(d => (
                <div key={d} className="text-center text-xs text-zinc-500 font-medium py-2">{d}</div>
              ))}
            </div>

            {/* Calendar Cells */}
            <div className="grid grid-cols-7">
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`empty-${i}`} className="h-24 border-b border-r border-white/5 bg-[#0A0A0A]/50" />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const dayEvents = getEventsForDay(day);
                const isToday = dateStr === today;
                const isSelected = dateStr === selectedDate;

                return (
                  <div
                    key={day}
                    className={`h-24 border-b border-r border-white/5 p-1 cursor-pointer transition-colors ${
                      isSelected ? 'bg-[#F5A623]/10 border-[#F5A623]/30' : isToday ? 'bg-white/[0.03]' : 'hover:bg-white/[0.02]'
                    }`}
                    onClick={() => setSelectedDate(dateStr)}
                    data-testid={`calendar-day-${day}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                        isToday ? 'bg-[#F5A623] text-black' : 'text-zinc-400'
                      }`}>
                        {day}
                      </span>
                      {dayEvents.length > 0 && (
                        <span className="text-[10px] text-zinc-500">{dayEvents.length}</span>
                      )}
                    </div>
                    <div className="space-y-0.5 overflow-hidden">
                      {dayEvents.slice(0, 3).map(ev => (
                        <div key={ev.id} className="flex items-center gap-1">
                          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${getEventColor(ev.type)}`} />
                          <span className="text-[10px] text-zinc-300 truncate">{ev.title}</span>
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <span className="text-[10px] text-zinc-500">+{dayEvents.length - 3} more</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 px-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#F5A623]" />
              <span className="text-xs text-zinc-500">Shoot</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-rose-500" />
              <span className="text-xs text-zinc-500">Deadline</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-xs text-zinc-500">Follow-up</span>
            </div>
          </div>
        </div>

        {/* Side Panel: Events for selected date */}
        <div>
          <Card className="bg-[#121212] border-white/10 sticky top-24">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <CalendarIcon className="h-5 w-5 text-[#F5A623]" />
                <h3 className="text-white font-medium">
                  {selectedDate
                    ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                    : 'Select a date'}
                </h3>
              </div>

              {!selectedDate ? (
                <p className="text-sm text-zinc-500 text-center py-8">Click on a date to see events</p>
              ) : selectedEvents.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-8">No events on this day</p>
              ) : (
                <div className="space-y-3" data-testid="selected-date-events">
                  {selectedEvents.map(ev => (
                    <div key={ev.id} className="p-3 bg-[#0A0A0A] rounded-lg border border-white/5">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <div className={`w-2 h-2 rounded-full ${getEventColor(ev.type)}`} />
                        <span className="text-white text-sm font-medium">{ev.title}</span>
                        {ev.assigned_to_name && ev.type === 'followup' && (
                          <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/20">
                            {ev.assigned_to_name}
                          </Badge>
                        )}
                      </div>
                      {ev.project_title && (
                        <p className={`text-xs ${getBrandColor(ev.brand)} mb-1`}>
                          {ev.project_title} ({ev.brand === 'triobliss' ? 'Trio Bliss' : 'Wittyshadows'})
                        </p>
                      )}
                      {ev.timing && ev.type === 'shoot' && (
                        <div className="flex items-center gap-1 text-xs text-zinc-400 mb-1">
                          {ev.timing === 'night' ? <Moon className="h-3 w-3" /> : <Sun className="h-3 w-3" />}
                          <span className="capitalize">{ev.timing} Shoot</span>
                        </div>
                      )}
                      {ev.team && ev.team.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-zinc-400 mt-1">
                          <Users className="h-3 w-3" />
                          <span>{ev.team.join(', ')}</span>
                        </div>
                      )}
                      {ev.freelancers && ev.freelancers.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-zinc-400 mt-1">
                          <Users className="h-3 w-3 text-amber-400" />
                          <span className="text-amber-400">Freelancers: {ev.freelancers.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
